import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Home,
  Building2,
  TrendingUp,
  LogOut,
  Users,
  CreditCard,
  Plus,
  X,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Bell,
  Star,
  Wallet,
  Mail,
  User,
  Heart,
  Calendar,
  Store,
  ShoppingBag,
  Settings,
  Shield,
  CheckCircle,
  Clock,
  Briefcase,
  Sun,
  Target,
  Landmark,
} from 'lucide-react';
import { logout } from '../../store/slices/authSlice';
import { RootState } from '../../store';
import { getNavigationForRole, groupNavigationByCategory, NavigationItem } from '../../utils/rbac';
import DiasporawiseLogo from '../../assets/DWBRANDprview.png';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

// Icon mapping for dynamic navigation
const iconMap: Record<string, any> = {
  Home,
  Building2,
  TrendingUp,
  Users,
  CreditCard,
  Plus,
  MessageSquare,
  Bell,
  Star,
  Wallet,
  Mail,
  User,
  Heart,
  Calendar,
  Store,
  ShoppingBag,
  Settings,
  Shield,
  CheckCircle,
  Clock,
  Briefcase,
  Sun,
  Target,
  Landmark,
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem('sidebarWidth');
    return savedWidth ? parseInt(savedWidth, 10) : 256; // Default 256px (w-64)
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Get navigation items based on user role
  const userNavigationItems = getNavigationForRole(user?.role as any);

  const navigationGroups = groupNavigationByCategory(userNavigationItems);

  // Save width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarWidth', sidebarWidth.toString());
  }, [sidebarWidth]);

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      const minWidth = 200; // Minimum width
      const maxWidth = 500; // Maximum width

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(name => name !== groupName)
        : [...prev, groupName]
    );
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };



  return (
    <>
      {/* Mobile/Tablet overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 xl:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div 
        ref={sidebarRef}
        className={`
          fixed xl:static inset-y-0 left-0 z-50 bg-white border-r border-gray-200 flex flex-col 
          transform transition-all duration-300 ease-in-out shadow-xl xl:shadow-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full xl:translate-x-0'}
        `}
        style={{
          width: sidebarCollapsed ? '80px' : `${sidebarWidth}px`,
          transition: isResizing ? 'none' : 'width 0.3s ease-in-out',
        }}
      >

        {/* Header Section */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <img src={DiasporawiseLogo} alt="Logo" className="w-5 h-5" />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-base font-bold text-gray-900">
                    DIASPORA<span className="text-blue-600">WISE</span>
                  </h1>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden xl:flex p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                {sidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
              </button>
              <button
                className="xl:hidden p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
                onClick={onToggle}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-4">
            {/* Notifications Section */}

            {Object.entries(navigationGroups).map(([groupName, items]) => (
              <div key={groupName}>
                {!sidebarCollapsed && (
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[11px] font-extrabold text-gray-500 uppercase tracking-widest">
                      {groupName}
                    </h3>
                    <button
                      onClick={() => toggleGroup(groupName)}
                      className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      {collapsedGroups.includes(groupName) ? (
                        <ChevronRight className="w-2.5 h-2.5" />
                      ) : (
                        <ChevronDown className="w-2.5 h-2.5" />
                      )}
                    </button>
                  </div>
                )}

                {!collapsedGroups.includes(groupName) && (
                  <ul className="space-y-0.5">
                    {items.map((item: NavigationItem) => {
                      const IconComponent = iconMap[item.icon] || Home;
                      const active = location.pathname === item.to;
                      return (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            end={item.to === '/admin'}
                            onClick={() => {
                              if (window.innerWidth < 1024) {
                                onToggle();
                              }
                            }}
                            className={`group flex items-center rounded-lg transition-all duration-200 text-sm relative overflow-hidden
                              ${sidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}
                              ${active ? 'bg-blue-100 text-blue-50 font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'}`}
                          >
                            <IconComponent className={`w-4 h-4 transition-colors duration-200 ${active ? 'text-blue-50' : 'text-gray-400 group-hover:text-gray-600'}`} />
                            {!sidebarCollapsed && (
                              <span className="block truncate flex-1">{item.label}</span>
                            )}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center rounded-lg transition-all duration-200 text-sm font-medium text-red-600 hover:bg-red-50/60
              ${sidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'}`}
          >
            <LogOut className="w-4 h-4 text-red-500" />
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>

        {/* Resize Handle */}
        {!sidebarCollapsed && (
          <div
            onMouseDown={handleResizeStart}
            className="hidden xl:flex absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-blue-100 bg-transparent transition-colors duration-200 group z-10"
            style={{ touchAction: 'none' }}
            title="Drag to resize sidebar"
          >
            <div className="absolute right-0.5 top-1/2 -translate-y-1/2 w-0.5 h-20 bg-gray-300 rounded-full group-hover:bg-blue-500 transition-colors duration-200" />
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;